/**
 * RISKS-UNITS-FLOW
 * Organisational chart of the company
 */
import { t } from "i18next"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import Button from "@/components/button"
import "@xyflow/react/dist/style.css"
import SaveIcon from "@/components/save-icon"
import unitStore from "@/features/risks/entities/unit/model/unit.store"
import PageHeader from "@/components/page-header"
import { useMemo, useState, useEffect } from "react"
import { createEmptyUnit, Unit } from "@/features/risks/entities/unit/model/unit.types"
import riskStore from "../../risk/model/risk.store"
import UnitSearch from "./UnitsSearch"
import UnitAddModal from "./modals/UnitAddModal"
import UnitNodeCard from "./UnitNodeCard"
import UnitListRow from "./UnitListRow"
import { useNavigate } from "react-router-dom"
import ListButton from "@/components/list-button"
import { faGripVertical, faList, faSitemap } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
// import UnitNodeTooltip from "./UnitNodeTooltip"

export type ActiveModal = null | "unitEdit" | "wizard"
export type ViewMode = "grid" | "list"

export default function RisksUnitsFlow() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const risks = useAppSelector(riskStore.selectors.list)

  const [activeModal, setActiveModal] = useState<ActiveModal>(null)
  const [hoveredUnit, setHoveredUnit] = useState<string | null>(null)
  const [searchedUnitId, setSearchedUnitId] = useState<string | null>(null)
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem("units-view-mode")
    return (saved as ViewMode) || "grid"
  })

  useEffect(() => {
    localStorage.setItem("units-view-mode", viewMode)
  }, [viewMode])

  const status = useAppSelector((state) => state.unit.status)
  const units = useAppSelector(unitStore.selectors.list)

  const sortedUnits = useMemo(() => {
    return [...units]
      .map((u) => {
        const validatedRisks = risks.filter((r) => r.UnitId === u.id && r.dateValidated)
        const maxResidualValue = Math.max(...validatedRisks.map((r) => r.valueEffective ?? 0), 0)
        return { ...u, _maxResidualValue: maxResidualValue }
      })
      .sort((a, b) => {
        // 1) size desc
        if ((b.size ?? 0) !== (a.size ?? 0)) {
          return (b.size ?? 0) - (a.size ?? 0)
        }
        // 2) max residual value desc
        return b._maxResidualValue - a._maxResidualValue
      })
  }, [units, risks])

  const handleCreateUnit = () => {
    dispatch(unitStore.actions.setActive(createEmptyUnit()))
    setActiveModal("wizard")
  }

  const handleClose = () => {
    setActiveModal(null)
  }

  const displayedUnits = useMemo(() => {
    if (searchedUnitId) {
      return sortedUnits.filter((u) => u.id === searchedUnitId)
    }
    return sortedUnits
  }, [sortedUnits, searchedUnitId])

  return (
    <>
      <PageHeader
        title={
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faSitemap} className="grey-t" />
            {t("units")}
          </div>
        }
        subtitle={t("units_pageheader_subtitle")}
        extraBelowSubtitle={
          units.length > 0 && (
            <div className="flex w-full items-center gap-4">
              <UnitSearch
                onSelectUnit={(unit: Unit | null) => {
                  setSearchedUnitId(unit?.id ?? null)
                }}
              />
              <div className="flex items-center gap-2">
                <ListButton icon={faGripVertical} isPrimary={viewMode === "grid"} onClick={() => setViewMode("grid")} />
                <span className="text-sm text-gray-600">Vue en grille</span>
              </div>
              <div className="flex items-center gap-2">
                <ListButton icon={faList} isPrimary={viewMode === "list"} onClick={() => setViewMode("list")} />
                <span className="text-sm text-gray-600">Vue en liste</span>
              </div>
            </div>
          )
        }>
        <div className="flex -mr-9">
          {units.length > 0 && (
            <Button onClick={handleCreateUnit} className="primary" isFullWidth icon={faSitemap}>
              {t("units_utils_create_new_node_short")}
            </Button>
          )}
        </div>
      </PageHeader>

      {/* --- Create Units (empty state) --- */}
      {units.length === 0 && (
        <div className="flex flex-col items-center justify-center h-[65vh] gap-6 overflow-hidden">
          <h2>{t("units_empty")}</h2>
          <img src={require("@/assets/start.png")} alt="start" className="w-1/4" />
          <Button className="primary" isLarge onClick={handleCreateUnit} icon={faSitemap} isFullWidth>
            {t("units_utils_create_new_node")}
          </Button>
          <SaveIcon status={status} isStatusDisplayed />
        </div>
      )}

      {/* --- Cards --- */}
      {displayedUnits.length > 0 && (
        <div
          className={
            viewMode === "grid"
              ? "relative grid gap-x-16 gap-y-16 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 items-stretch auto-rows-fr"
              : "relative flex flex-col gap-3"
          }>
          {displayedUnits.map((u) => {
            if (viewMode === "list") {
              return (
                <UnitListRow
                  key={u.id}
                  unit={u}
                  onClick={() => {
                    dispatch(unitStore.actions.setActive(u))
                    navigate(`/risks/unit/${u.id}`, { state: { backTo: "/risks/units" } })
                  }}
                />
              )
            }

            return (
              <div
                key={u.id}
                className="relative h-full"
                onMouseEnter={() => setHoveredUnit(u.id)}
                onMouseLeave={() => setHoveredUnit(null)}>
                <UnitNodeCard
                  hovered={hoveredUnit === u.id}
                  unit={u}
                  onClick={() => {
                    dispatch(unitStore.actions.setActive(u))
                    navigate(`/risks/unit/${u.id}`, { state: { backTo: "/risks/units" } })
                  }}
                />

                {/* {hoveredUnit === u.id && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-12 w-full z-[9999]">
                    <UnitNodeTooltip unitId={u.id} />
                  </div>
                )} */}
              </div>
            )
          })}
        </div>
      )}

      {/* --- Modale wizard --- */}
      {activeModal === "wizard" && <UnitAddModal addRisks onClose={handleClose} />}
    </>
  )
}
